import TR from 'utils/TR';
import T from 'utils/T';
import ex from 'utils/ex';
import DIRECTION_VERTICAL from 'constants/DIRECTION_VERTICAL';
import Section from 'components/common/Section';
import HBox from 'components/common/HBox';
import SortButton from 'components/common/SortButton';
import useSort from 'hooks/tables/useSort';
import DialogButton from 'components/common/DialogButton';
import BloodGlocoseDialog from '../../../dialogs/BloodGlocoseDialog';
import Recoil from 'recoil';
import * as AppStore from 'stores/AppStore';
import React from 'react';
import useReportBolus from 'hooks/communication/useReportBolus';
import usePaging from 'hooks/tables/usePaging';
import SORT_DESCENDING from 'constants/SORT_DESCENDING';
import SORT_DEFAULT from 'constants/SORT_DEFAULT';
import PagingIndicator from 'components/common/paging/PagingIndicator';
import defaultRenderPrevButtons from 'components/common/paging/defaultRenderPrevButtons';
import defaultRenderNextButtons from 'components/common/paging/defaultRenderNextButtons';
import * as Luxon from 'luxon'
import { luxonClone } from 'utils/time';
import SelectButton from 'components/common/SelectButton';
import DAY_ZONE_TYPES from 'constants/DAY_ZONE_TYPES';
import GLOCOSE_TYPES from 'constants/GLOCOSE_TYPES';
import Dot from 'components/common/Dot';
import ABNORMAL_GLOCOSE_COLORS from 'constants/ABNORMAL_GLOCOSE_COLORS';
import ExerciseDialog from 'components/dialogs/ExerciseDialog';
import WEEK_DAY_COLUMNS from 'constants/WEEK_DAY_COLUMNS';
import useSetChartData from 'hooks/app/useSetChartData';

const DEFAULT_SORT_LIST = [{ sort_by: '1', order_by: SORT_DESCENDING }];
const PAGE_ROW_CNT = 5;

export default function InsulinAnalysis(props) {
  const {
    category,
    ...rest
  } = props;

  const {
    order: dateOrder,
    onOrderChange: onDateOrderChange,
  } = useSort(false, SORT_DESCENDING);

  const [sort_list, setSortList] = React.useState(() => DEFAULT_SORT_LIST)
  React.useEffect(() => {
    if (window.NB_CHANGED_LOG) console.log('DietMealAnalysis.1', dateOrder);
    const additionals = dateOrder === SORT_DEFAULT ? [] : [{ sort_by: '1', order_by: dateOrder }];
    setSortList(list => [...additionals, ...list.filter(({ sort_by }) => sort_by !== '1')])
  }, [dateOrder]);

  const {
    page,
    onPage,
    npage,
    onNPage,
    maxPage,
    onMaxPage
  } = usePaging({
    resetCondition: [],
    defaultMaxPage: 6,
  });

  const {
    requestReportBolus,
  } = useReportBolus();

  const [abnormal_bld_sugr_tp_cd, setAbnormalBloodSugarTpCd] = React.useState('2');
  const onAbnormalTypeChange = React.useCallback((e) => {
    setAbnormalBloodSugarTpCd(() => e.target.value);
    onPage(1);
  }, [onPage]);

  const setChartData = useSetChartData('InsulinBolusAnalysisList')
  const [list, setList] = React.useState([]);
  const period_start = Recoil.useRecoilValue(AppStore.assets.period_start);
  const period_end = Recoil.useRecoilValue(AppStore.assets.period_end);
  const patient_no = Recoil.useRecoilValue(AppStore.assets.patient_no);
  React.useEffect(() => {
    requestReportBolus({
      patient_no,
      search_start_time: period_start,
      search_end_time: period_end,
      page_row_cnt: PAGE_ROW_CNT,
      page_no: npage,
      abnormal_bld_sugr_tp_cd,
      sort_list,
    }, (success, data) => {
      if (success) {
        setChartData(() => ({ list: data.bolus_analysis_list }));
        setList(() => data.bolus_analysis_list);
        onMaxPage(data.max_page_no || 1);
        onPage(data.page_no || 1);
      } else {
        setChartData(() => ({ list: [] }));
        setList(() => []);
        onMaxPage(1);
        onPage(1);
      }
    });
  }, [abnormal_bld_sugr_tp_cd, npage, onMaxPage, onPage, patient_no, period_end, period_start, requestReportBolus, setChartData, sort_list]);

  return (
    <Section
      {...ex('InsulinAnalysis', rest)}
      {...{
        caption: T('볼루스 분석'),
        options: (
          <>
            <HBox full>
              {/* {T('| 이상혈당 발생내역')} */}
            </HBox>
            <div>
              <SelectButton
                {...{
                  onChange: onAbnormalTypeChange,
                  value: abnormal_bld_sugr_tp_cd,
                  options: [{
                    value: '1',
                    label: '전체',
                  }, {
                    value: '2',
                    label: '이상혈당',
                  }, {
                    value: '3',
                    label: '고혈당',
                  }, {
                    value: '5',
                    label: '저혈당',
                  }, {
                    value: '4',
                    label: '변동혈당',
                  }],
                }}
              />
            </div>
          </>
        ),
        direction: DIRECTION_VERTICAL,
      }}
    >
      <table>
        <thead>
          <tr>
            <th>
              <div>
                <div>
                  {T('날짜')}
                </div>
                <div>
                  <SortButton {...{ order: dateOrder, onClick: onDateOrderChange }} />
                </div>
              </div>
            </th>
            <th>
              <div>
                {T('시간대')}
              </div>
            </th>
            <th>
              <div>
                {T('투여량')}
              </div>
            </th>
            <th>
              <div>
                {T('투여시점 혈당')}
              </div>
            </th>
            <th>
              <div>
                {T('이상혈당 발생')}
              </div>
            </th>
            <th>
              <div>
                {T('이상혈당 발생까지 시간(분)')}
              </div>
            </th>

            <th>
              <div>
                {T('1시간내 식사여부')}
              </div>
            </th>
            <th>
              <div>
                {T('2시간내 활동(보)')}
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          {list.map((item, index) => {
            const {
              time,
              day_tp_cd,
              time_zone_tp_cd,
              bolus_amt,
              bldsugr,
              abnormal_bldsugr_maintain_time,
              abnormal_bldsugr_tp,
              before_abnormal_bldsugr_meal_yn,
              before_abnormal_bldsugr_active_min,
            } = item;
            const luxon = Luxon.DateTime.fromFormat(time, 'yyyyMMddhhmmss');
            const date = luxon.toFormat("yy.MM.dd");
            const hhmm = luxon.toFormat("HH:mm");
            const weekday = luxonClone(luxon).weekday;

            return (
              <tr key={index}>
                <td>
                  <div>
                    <DialogButton
                      {...{ category: 'App' }}
                      {...ex('ExerciseDialogButton')}
                      {...{ label: T('{0}({1}) {2}', date, WEEK_DAY_COLUMNS[weekday - 1], hhmm) }}
                    >
                      <ExerciseDialog
                        {...{
                          luxon,
                        }}
                      />
                    </DialogButton>
                  </div>
                </td>
                <td>
                  <div>
                    {DAY_ZONE_TYPES[time_zone_tp_cd - 1]}
                  </div>
                </td>
                <td>
                  <div>
                    {bolus_amt}
                  </div>
                </td>
                <td>
                  <div>
                    {bldsugr}
                  </div>
                </td>

                <td>
                  <div>
                    {GLOCOSE_TYPES[abnormal_bldsugr_tp]}<Dot {...{ color: ABNORMAL_GLOCOSE_COLORS[abnormal_bldsugr_tp] }} />
                  </div>
                </td>
                <td>
                  <div>
                    {abnormal_bldsugr_maintain_time}
                  </div>
                </td>

                <td>
                  <div>
                    {before_abnormal_bldsugr_meal_yn ? '○' : ''}
                  </div>
                </td>
                <td>
                  <div>
                    {before_abnormal_bldsugr_active_min}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <PagingIndicator
        {...{
          indicatorCount: 5,
          maxPage,
          page,
          onNPage,
          renderPrevButtons: defaultRenderPrevButtons,
          renderNextButtons: defaultRenderNextButtons,
        }}
      />
    </Section>
  );
}
